import { type Message as MessageType } from "@/reducers/messagesSlice"
import { useMessageData } from "@/components/chat/useMessageData"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import React, { memo, useCallback, useContext, useState } from "react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"
import { Message } from "@/components/ui/message"
import { updateRouteAlert } from "@/reducers/overlaysSlice"
import { selectAiWidgetMap } from "@/reducers/aiWidgetMapSlice"
import { ChevronRight, ChevronUp, EyeOff } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { FinishAlertBade } from "@/components/maps/FinishAlertBadge"
import { FinishAlertSummary } from "@/components/maps/FinishAlertSummary"
import { useFeatureFlagEnabled } from "posthog-js/react"
import useMessageActions from "./useMessageActions"
import type { ComposerSurface } from "@/reducers/chatRepliesSlice"
import { cn } from "@/lib/utils"
import StructuredMessageRenderer from "@/components/slickAI/structuredMessage/StructuredMessageRenderer"
import css from "./SlickAIMessage.module.css"
import Markdown from "markdown-to-jsx"
import { toAgentMarkdown } from "@/lib/markdownText"
import { isMobile } from "@/utils/detectDevice"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import SlickAIGalleryLink from "@/components/chat/SlickAIGalleryLink"
import FindDriversResult, {
  isFindDriversResult,
} from "@/components/chat/FindDriversResult"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"
import { AIChatShowMapButton } from "@/components/ai-chat/AIChatShowMapButton"
import { AI_CHAT_PROSE_CLASS } from "@/components/ai-chat/aiChatProse"
import { MESSAGE_ROW_INSET } from "@/components/ui/message/rowInset"
import { AIChatAnswer } from "@/components/ai-chat/AIChatAnswer"
import { AIChatTurn } from "@/components/ai-chat/AIChatTurnStatus"
import { selectFinishedTurn } from "@/reducers/agentPipelineSlice"
import { MessageBody } from "@/components/ui/message/message-body"
import { detectType } from "@/components/ui/message/message-file"
import CarReportBadge from "@/components/chat/CarReportBadge"
import { carReportBody, carReportBubbleClass } from "@/lib/carReport"

/**
 * Convert bare URLs in text to markdown links so markdown-to-jsx renders them as clickable.
 * Skips URLs already inside markdown link syntax [text](url) or <url>.
 */
export function linkifyUrls(text: string): string {
  // 1. Clean trailing markdown artifacts (* or **) from URLs inside markdown links
  //    e.g. [text](https://example.com**) → [text](https://example.com)
  const cleaned = text.replace(/(\[[^\]]*\]\([^)]*?)\*{1,2}\)/g, "$1)")
  // 2. Match markdown links or angle-bracket URLs first (to skip them), then capture bare URLs.
  //    Avoids lookbehind assertions which are unsupported in Safari.
  return cleaned.replace(
    /\[[^\]]*\]\([^)]*\)|<[^>]*>|(https?:\/\/[^\s\])<>*]+)/g,
    (match, bareUrl) => (bareUrl ? `[${bareUrl}](${bareUrl})` : match),
  )
}

export type SlickAIMessageProps = {
  message: MessageType
  grouped?: boolean
  isLastInGrouped?: boolean
  interactive?: boolean
  actionButtonsVisible?: boolean
  hideActionButtons?: () => void
  // The previous row is also an internal Slick AI message, same day — still
  // the same conversation, so skip the extra spacing that otherwise sets
  // the AI layer apart from driver bubbles.
  tightenGapAbove?: boolean
  composerSurface?: ComposerSurface
}

const SlickAIMessage = ({
  message,
  grouped,
  isLastInGrouped,
  interactive = true,
  actionButtonsVisible,
  hideActionButtons,
  tightenGapAbove,
  composerSurface,
}: SlickAIMessageProps) => {
  const messageData = useMessageData(message)
  const dispatch = useAppDispatch()
  const { isForwardingMode } = useContext(ChatHistoryContext)

  // Route, polyline and reference-route data on the backend are keyed by the
  // driver ROOM jid. Picking a MUC participant heuristically can select an
  // ex-driver still lingering as a room member, which makes the alert maps
  // query another vehicle's (or an empty) track - so use the room the
  // message was posted in, and only fall back to the participant.
  const driverJid =
    message.from?.split("/")[0] || messageData.driverParticipant?.jid

  const actions = useMessageActions(message, undefined, composerSurface)

  const handleShowReferenceRouteAlert = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      dispatch(
        updateRouteAlert({
          visible: true,
          alertId: message.referenceRouteAlert?.alertId,
          driverJid,
          updatedAt: message.timestamp,
          type: "reference",
        }),
      )
    },
    [
      dispatch,
      driverJid,
      message.referenceRouteAlert?.alertId,
      message.timestamp,
    ],
  )

  const handleShowStopLateAlert = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      dispatch(
        updateRouteAlert({
          visible: true,
          alertId: message.stopLateAlert?.alertId,
          driverJid,
          updatedAt: message.timestamp,
          type: "late",
        }),
      )
    },
    [dispatch, driverJid, message.stopLateAlert?.alertId, message.timestamp],
  )

  const handleOpenAgentMap = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      if (message.aiWidgetMap) {
        dispatch(selectAiWidgetMap(message.aiWidgetMap))
      }
    },
    [dispatch, message.aiWidgetMap],
  )

  const handleShowFinishAlert = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      dispatch(
        updateRouteAlert({
          visible: true,
          alertId: message.finishedRouteAlert?.alertId,
          driverJid,
          updatedAt: message.timestamp,
          type: "routeFinished",
          details: message.finishedRouteAlert,
        }),
      )
    },
    [dispatch, driverJid, message.finishedRouteAlert, message.timestamp],
  )

  // The workspace layout (not the presence of a driver participant) decides
  // the modern AI look — same rule GenericMessage uses for the blue chip.
  // Requiring `driverParticipant` left some agent messages on the old violet
  // bubble whenever the participant heuristic missed.
  const inWorkspace = useIsDriverWorkspace()
  const { t } = useTranslation()

  const isInternal = Boolean(message.internal)
  // A file the agent shared (SIMS reference, the same stanza shape the app
  // sends). Its body is only the download URL, so rendering it as markdown
  // showed a raw link — and mangled names like `_S1_` into italics.
  const isFile = Boolean(message.file && message.url && message.fileName)
  const turnSummary = useAppSelector((state) =>
    selectFinishedTurn(state, message.id),
  )

  // A finished-order alert is long — gps's text, a document, a table — and a
  // room collects one per order. Behind the flag it starts as one line.
  const finishAlertCollapsible =
    Boolean(useFeatureFlagEnabled("finish-alert-collapsed")) &&
    Boolean(message.finishedRouteAlert) &&
    interactive
  const [finishAlertExpanded, setFinishAlertExpanded] = useState(false)
  const finishAlertCollapsed = finishAlertCollapsible && !finishAlertExpanded

  const renderAlertButton = (
    alertId: string | undefined,
    onClick: (event: React.MouseEvent) => void,
    additionalContent?: React.ReactNode,
  ) => {
    if (!alertId) return null

    return (
      <div className="pb-0 pt-1">
        {additionalContent}
        <button
          type="button"
          onClick={onClick}
          className="inline-flex items-center gap-0.5 text-sm text-gray-500 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-300"
        >
          {t("chat.slickAIMessage.showOnMap")}
          <ChevronRight className="size-3.5" />
        </button>
      </div>
    )
  }

  const renderMessageCTA = () => {
    return (
      <>
        {message.referenceRouteAlert &&
          renderAlertButton(
            message.referenceRouteAlert.alertId,
            handleShowReferenceRouteAlert,
          )}

        {message.stopLateAlert &&
          renderAlertButton(
            message.stopLateAlert.alertId,
            handleShowStopLateAlert,
          )}

        {message.finishedRouteAlert && (
          <div className="flex flex-col gap-2 pb-0 pt-2">
            <FinishAlertBade
              {...message.finishedRouteAlert}
              message={message}
            />
            {finishAlertCollapsible ? (
              <div className="flex items-center gap-4">
                {renderAlertButton(
                  message.finishedRouteAlert.alertId,
                  handleShowFinishAlert,
                )}
                <div className="pb-0 pt-1">
                  <button
                    type="button"
                    onClick={() => setFinishAlertExpanded(false)}
                    aria-expanded={true}
                    className="inline-flex items-center gap-0.5 text-sm text-gray-500 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    {t("chat.slickAIMessage.collapse")}
                    <ChevronUp className="size-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              renderAlertButton(
                message.finishedRouteAlert.alertId,
                handleShowFinishAlert,
              )
            )}
          </div>
        )}

        {message.aiWidgetMap &&
          (inWorkspace ? (
            <AIChatShowMapButton onClick={handleOpenAgentMap} />
          ) : (
            <div className="pb-0 pt-1">
              <button
                type="button"
                onClick={handleOpenAgentMap}
                className="inline-flex items-center gap-0.5 text-sm text-gray-500 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-gray-300"
              >
                {t("chat.slickAIMessage.showMap")}
                <ChevronRight className="size-3.5" />
              </button>
            </div>
          ))}
      </>
    )
  }

  const renderMarkdown = (text: string) => {
    // Workspace: same Streamdown stack as global Slick AI. Elsewhere: the
    // classic markdown-to-jsx path (flag-off / non-workspace rooms).
    if (inWorkspace) {
      const markdown = <AIChatAnswer text={text} isStreaming={false} />
      // Only markdown gets prose typography, and the surface decides which:
      // the Slick AI column has its own, a bubble has the bubble's. The
      // other branches of renderBody render their own layout (tables, cards,
      // file chips) and must stay outside prose, which would restyle every
      // element inside them.
      return isInternal ? (
        <div className={AI_CHAT_PROSE_CLASS}>{markdown}</div>
      ) : (
        <Message.Markdown>{markdown}</Message.Markdown>
      )
    }
    return (
      <div className={css.markdown}>
        <Markdown
          options={{
            overrides: {
              a: {
                component: SlickAIGalleryLink,
              },
            },
          }}
        >
          {toAgentMarkdown(linkifyUrls(text))}
        </Markdown>
      </div>
    )
  }

  // Same chip a dispatcher's or a driver's file gets (PDF preview, image,
  // generic file card), picked by the file name's extension. An extension
  // the app has no preview for still gets the generic card rather than the
  // bare URL. Any text besides the URL is a caption and stays markdown.
  const renderFile = () => {
    const caption = message.body.trim() === message.url ? "" : message.body
    return (
      <>
        <MessageBody type={detectType(message.fileName) ?? "unknown"} />
        {caption && <div className="mt-2">{renderMarkdown(caption)}</div>}
      </>
    )
  }

  const renderBody = () => {
    if (finishAlertCollapsed && message.finishedRouteAlert) {
      return (
        <FinishAlertSummary
          alert={message.finishedRouteAlert}
          onExpand={() => setFinishAlertExpanded(true)}
        />
      )
    }

    if (isFile) return renderFile()

    if (isFindDriversResult(message.body)) {
      return <FindDriversResult body={message.body} />
    }

    if (message.body.startsWith("{")) {
      try {
        const structuredMessage = JSON.parse(message.body)
        if (
          structuredMessage.blocks &&
          Array.isArray(structuredMessage.blocks)
        ) {
          return (
            <StructuredMessageRenderer
              key={message.id}
              message={structuredMessage}
              messageId={message.id}
            />
          )
        }
      } catch (e) {
        // Not a valid JSON or structured message, continue with normal rendering
      }
    }
    return renderMarkdown(carReportBody(message))
  }

  const haveReactions = messageData.reactions.length > 0
  // Internal (dispatcher <-> Slick AI) messages reuse the exact plain-text
  // style from the general Slick AI chat tab (AIChatMessage) — no bubble, no
  // avatar, no rail or "internal" label. The All/Driver/Internal filter tabs
  // above the thread are what tell the layers apart now. Outside the
  // workspace there are no such tabs, so the violet bubble and its
  // "Not visible for a driver" badge remain the only signal — keep them.
  if (isInternal && inWorkspace) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                MESSAGE_ROW_INSET,
                "mb-4 cursor-default",
                // Extra space above so this layer reads as distinct from
                // driver-facing bubbles. Skip it between same-day Slick AI
                // messages back to back — still the same conversation.
                tightenGapAbove ? "mt-1" : "mt-10",
              )}
            >
              <AIChatTurn
                summary={turnSummary}
                answer={
                  isFile ? (
                    // The file chip reads its message from the Message
                    // context, which this bubble-less layout has no other
                    // reason to set up. `m-0` drops the row's own margins
                    // and inset: this wrapper already is the row.
                    <Message
                      message={message}
                      interactive={false}
                      className="m-0"
                    >
                      {renderBody()}
                    </Message>
                  ) : (
                    renderBody()
                  )
                }
              />
              {interactive && !finishAlertCollapsed && renderMessageCTA()}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            {format(new Date(message.timestamp), "EEEE, MMM d, HH:mm", {
              locale: getDateFnsLocale(),
            })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // A driver-facing message (the driver actually got it) reads like any
  // other message in the conversation — same bubble, same side, just tagged
  // "Slick AI" as its author. In the workspace that bubble is the outgoing
  // (driver-chat) style; elsewhere it stays the classic violet AI bubble.
  return (
    <Message
      variant={inWorkspace ? "outgoing" : "ai"}
      message={message}
      // When forwarding mode is active, don't pass action handlers
      onReply={!isForwardingMode ? actions.handleReply : undefined}
      onForward={!isForwardingMode ? actions.handleForward : undefined}
      onReact={!isForwardingMode ? actions.handleReact : undefined}
      onCopy={!isForwardingMode && isMobile ? actions.handleCopy : undefined}
      grouped={grouped}
      showAvatar={isLastInGrouped && !isForwardingMode}
      actionButtonsVisible={actionButtonsVisible}
      hideActionButtons={hideActionButtons}
      className={cn({ "mb-6": haveReactions })}
    >
      {interactive && (
        <Message.Avatar
          src="slickAvatar.svg"
          name={messageData.authorName}
          fallback={messageData.authorInitials}
          avatarColor={messageData.avatarColor}
        />
      )}
      <Message.Bubble
        variant={inWorkspace ? "outgoing" : "ai"}
        className={carReportBubbleClass(message.carReport)}
      >
        <Message.Header
          author="Slick AI"
          timestamp={
            inWorkspace
              ? format(new Date(message.timestamp), "MMM d, HH:mm", {
                  locale: getDateFnsLocale(),
                })
              : new Date(message.timestamp)
          }
          hideTimestampUntilHover={inWorkspace}
        />
        <>
          {message.carReport && <CarReportBadge kind={message.carReport} />}
          {renderBody()}
          {interactive && !finishAlertCollapsed && renderMessageCTA()}
        </>
        {interactive && !inWorkspace && (
          <Message.Metadata>
            {message.internal && (
              <div className="flex items-center gap-1 text-violet-600">
                <EyeOff className="size-3" />
                {t("chat.slickAIMessage.notVisibleForDriver")}
              </div>
            )}
          </Message.Metadata>
        )}
        {messageData.reactions.length > 0 && interactive && (
          <Message.Reactions reactions={messageData.reactions} />
        )}
      </Message.Bubble>
      {/* Don't show actions when in forwarding mode */}
      {interactive && !isForwardingMode && <Message.Actions />}
    </Message>
  )
}

// Custom comparison function that ignores context changes that don't affect rendering
const areEqual = (
  prevProps: SlickAIMessageProps,
  nextProps: SlickAIMessageProps,
) => {
  // Compare message properties
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.body === nextProps.message.body &&
    prevProps.message.timestamp === nextProps.message.timestamp &&
    !!prevProps.message.aiWidgetMap === !!nextProps.message.aiWidgetMap &&
    prevProps.message.reactions.length === nextProps.message.reactions.length &&
    prevProps.message.reactions.every(
      (reaction, index) =>
        reaction.id === nextProps.message.reactions[index]?.id &&
        reaction.emojis.join(",") ===
          nextProps.message.reactions[index]?.emojis.join(","),
    ) &&
    prevProps.grouped === nextProps.grouped &&
    prevProps.isLastInGrouped === nextProps.isLastInGrouped &&
    prevProps.interactive === nextProps.interactive &&
    prevProps.actionButtonsVisible === nextProps.actionButtonsVisible
  )
}

export default memo(SlickAIMessage, areEqual)
